import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import { ThemeContext } from '@hig/theme-context';
import { createCustomClassNames } from '@hig/utils';
import { ControlBehavior } from '@hig/behaviors';

var targets = Object.freeze({
  SELF: "_self",
  BLANK: "_blank",
  PARENT: "_parent",
  TOP: "_top"
});

var types = Object.freeze({
  FLAT: "flat",
  OUTLINE: "outline",
  SOLID: "solid",
  /** @deprecated */
  PRIMARY: "primary",
  /** @deprecated */
  SECONDARY: "secondary"
});

var widths = Object.freeze({
  SHRINK: "shrink",
  GROW: "grow"
});

var availableTargets = Object.freeze(Object.values(targets));
var availableTypes = Object.freeze(Object.values(types));
var availableWidths = Object.freeze(Object.values(widths));

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function getButtonDefaultButtonRules(themeData) {
  return {
    borderWidth: themeData["button.borderWidth"],
    borderStyle: "solid",
    borderColor: "transparent",
    borderRadius: themeData["button.borderRadius"],
    boxSizing: "border-box",
    color: themeData["button.solid.label.fontColor"],
    cursor: "pointer",
    display: "inline-block",
    fontFamily: themeData["button.label.fontFamily"],
    fontSize: themeData["button.label.fontSize"],
    fontWeight: themeData["button.label.fontWeight"],
    lineHeight: themeData["button.label.lineHeight"],
    margin: "0",
    overflow: "hidden",
    padding: themeData["button.paddingVertical"] + "\n      " + themeData["button.paddingHorizontal"],
    position: "relative",
    textAlign: "center",
    textDecoration: "none",
    textOverflow: "ellipsis",
    transitionDuration: "0.3s",
    transitionProperty: "box-shadow",
    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
    userSelect: "none",
    whiteSpace: "nowrap",
    MozOsxFontSmoothing: "grayscale",
    WebkitFontSmoothing: "antialiased"
  };
}

function getButtonRulesByType(type, themeData) {
  switch (type) {
    case types.SOLID:
    case types.PRIMARY:
      {
        return {
          background: themeData["button.solid.default.backgroundColor"],
          color: themeData["button.solid.label.fontColor"],
          "&:visited": {
            color: themeData["button.solid.label.fontColor"]
          },
          "svg *": {
            fill: themeData["button.solid.iconColor"]
          }
        };
      }
    case types.OUTLINE:
    case types.SECONDARY:
      {
        return {
          background: "none",
          borderColor: themeData["button.outline.default.borderColor"],
          color: themeData["button.outline.label.fontColor"],
          "&:visited": {
            color: themeData["button.outline.label.fontColor"]
          },
          "svg *": {
            fill: themeData["button.outline.iconColor"]
          }
        };
      }
    case types.FLAT:
      {
        return {
          background: "none",
          borderColor: themeData["button.flat.default.borderColor"],
          color: themeData["button.flat.label.fontColor"],
          "&:visited": {
            color: themeData["button.flat.label.fontColor"]
          },
          "svg *": {
            fill: themeData["button.flat.iconColor"]
          }
        };
      }
    default:
      return {};
  }
}

function getButtonRulesByDisabled(type, themeData) {
  return {
    opacity: themeData["colorScheme.opacity.disabled"],
    cursor: "default",
    pointerEvents: "none"
  };
}

function getButtonRulesByWidth(width) {
  return width === widths.GROW ? { width: "100%" } : {};
}

function getButtonHoverRulesByType(type, themeData) {
  switch (type) {
    case types.SOLID:
    case types.PRIMARY:
      return {
        background: themeData["button.solid.hover.backgroundColor"],
        boxShadow: "0 0 0 " + themeData["button.haloWidth"] + " " + themeData["button.hover.haloColor"]
      };
    case types.OUTLINE:
    case types.SECONDARY:
      return {
        borderColor: themeData["button.outline.hover.borderColor"],
        boxShadow: "0 0 0 " + themeData["button.haloWidth"] + " " + themeData["button.hover.haloColor"]
      };
    case types.FLAT:
      return {
        borderColor: themeData["button.flat.hover.borderColor"],
        boxShadow: "0 0 0 " + themeData["button.haloWidth"] + " " + themeData["button.hover.haloColor"]
      };
    default:
      return {};
  }
}

function getButtonFocusRulesByType(type, themeData) {
  switch (type) {
    case types.SOLID:
    case types.PRIMARY:
      return {
        background: themeData["button.solid.focus.backgroundColor"],
        boxShadow: "0 0 0 " + themeData["button.haloWidth"] + " " + themeData["button.focus.haloColor"],
        outline: "none"
      };
    case types.OUTLINE:
    case types.SECONDARY:
      return {
        background: themeData["button.outline.focus.backgroundColor"],
        borderColor: themeData["button.outline.focus.borderColor"],
        boxShadow: "0 0 0 " + themeData["button.haloWidth"] + " " + themeData["button.focus.haloColor"],
        color: themeData["button.outline.label.fontColor"],
        outline: "none",
        "svg *": {
          fill: themeData["button.outline.iconColor"]
        }
      };
    case types.FLAT:
      return {
        background: themeData["button.flat.focus.backgroundColor"],
        borderColor: themeData["button.flat.focus.borderColor"],
        boxShadow: "0 0 0 " + themeData["button.haloWidth"] + " " + themeData["button.focus.haloColor"],
        color: themeData["button.flat.label.fontColor"],
        outline: "none",
        "svg *": {
          fill: themeData["button.flat.iconColor"]
        }
      };
    default:
      return {};
  }
}

function getButtonPressedRulesByType(type, themeData) {
  switch (type) {
    case types.SOLID:
    case types.PRIMARY:
      return {
        boxShadow: "0 0 0 " + themeData["button.pressed.haloWidth"] + " " + themeData["button.pressed.haloColor"]
      };
    case types.OUTLINE:
    case types.SECONDARY:
      return {
        borderColor: themeData["button.outline.pressed.borderColor"],
        boxShadow: "0 0 0 " + themeData["button.pressed.haloWidth"] + " " + themeData["button.pressed.haloColor"]
      };
    case types.FLAT:
      return {
        boxShadow: "0 0 0 " + themeData["button.pressed.haloWidth"] + " " + themeData["button.pressed.haloColor"]
      };
    default:
      return {};
  }
}

function stylesheet(props, themeData, theme) {
  var disabled = props.disabled,
      hasFocus = props.hasFocus,
      hasHover = props.hasHover,
      isPressed = props.isPressed,
      type = props.type,
      width = props.width;

  var iconSize = theme.densityId === "medium-density" ? "24px" : "16px";

  return {
    button: _extends({}, getButtonDefaultButtonRules(themeData), getButtonRulesByType(type, themeData), disabled ? getButtonRulesByDisabled(type, themeData) : _extends({}, hasFocus ? getButtonFocusRulesByType(type, themeData) : {}, hasHover ? getButtonHoverRulesByType(type, themeData) : {}, isPressed ? getButtonPressedRulesByType(type, themeData) : {}), getButtonRulesByWidth(width)),
    icon: {
      display: "inline-block",
      position: "absolute",
      height: iconSize,
      marginRight: themeData["button.gutterWidth"],
      top: "50%",
      transform: "translateY(-50%)"
    },
    iconText: {
      marginLeft: "calc(" + iconSize + " + " + themeData["button.gutterWidth"] + ")"
    }
  };
}

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var ButtonPresenter = function (_Component) {
  _inherits(ButtonPresenter, _Component);

  function ButtonPresenter() {
    _classCallCheck(this, ButtonPresenter);

    return _possibleConstructorReturn(this, (ButtonPresenter.__proto__ || Object.getPrototypeOf(ButtonPresenter)).apply(this, arguments));
  }

  _createClass(ButtonPresenter, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _props = this.props,
          disabled = _props.disabled,
          hasFocus = _props.hasFocus,
          hasHover = _props.hasHover,
          isPressed = _props.isPressed,
          icon = _props.icon,
          link = _props.link,
          onBlur = _props.onBlur,
          onClick = _props.onClick,
          onFocus = _props.onFocus,
          onHover = _props.onHover,
          onMouseDown = _props.onMouseDown,
          onMouseEnter = _props.onMouseEnter,
          onMouseLeave = _props.onMouseLeave,
          onMouseUp = _props.onMouseUp,
          customStylesheet = _props.stylesheet,
          target = _props.target,
          title = _props.title,
          type = _props.type,
          width = _props.width,
          otherProps = _objectWithoutProperties(_props, ["disabled", "hasFocus", "hasHover", "isPressed", "icon", "link", "onBlur", "onClick", "onFocus", "onHover", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseUp", "stylesheet", "target", "title", "type", "width"]);

      var className = otherProps.className,
          htmlTabIndex = otherProps.tabIndex;


      var href = link || undefined;
      var tabIndex = disabled ? "-1" : htmlTabIndex || "0";
      var Wrapper = link ? "a" : "button";
      var wrapperTarget = link ? target : undefined;
      var iconClassName = createCustomClassNames(className, "icon");
      var iconTextClassName = createCustomClassNames(className, "icon-text");

      return React.createElement(
        ThemeContext.Consumer,
        null,
        function (_ref) {
          var resolvedRoles = _ref.resolvedRoles,
              metadata = _ref.metadata;

          var styles = stylesheet({ disabled: disabled, hasFocus: hasFocus, hasHover: hasHover, isPressed: isPressed, type: type, width: width }, resolvedRoles, metadata);
          var cssStyles = customStylesheet ? customStylesheet(styles, _this2.props, resolvedRoles, metadata) : styles;

          return React.createElement(
            Wrapper,
            _extends$1({}, otherProps, {
              className: cx(css(cssStyles.button), className),
              href: href,
              tabIndex: tabIndex,
              target: wrapperTarget,
              onBlur: onBlur,
              onClick: onClick,
              onFocus: onFocus,
              onMouseDown: onMouseDown,
              onMouseEnter: onMouseEnter,
              onMouseLeave: onMouseLeave,
              onMouseOver: onHover,
              onMouseUp: onMouseUp,
              disabled: disabled
            }),
            icon && React.createElement(
              "span",
              { className: cx(css(cssStyles.icon), iconClassName) },
              icon
            ),
            React.createElement(
              "span",
              {
                className: icon ? cx(css(cssStyles.iconText), iconTextClassName) : ""
              },
              title
            )
          );
        }
      );
    }
  }]);

  return ButtonPresenter;
}(Component);

ButtonPresenter.propTypes = {
  disabled: PropTypes.bool,
  hasFocus: PropTypes.bool,
  hasHover: PropTypes.bool,
  isPressed: PropTypes.bool,
  icon: PropTypes.node,
  link: PropTypes.string,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onHover: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseUp: PropTypes.func,
  stylesheet: PropTypes.func,
  target: PropTypes.oneOf(availableTargets),
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(availableTypes),
  width: PropTypes.oneOf(availableWidths)
};
ButtonPresenter.__docgenInfo = {
  "description": "",
  "displayName": "ButtonPresenter",
  "props": {
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasFocus": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasHover": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "isPressed": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "icon": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "link": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onHover": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseDown": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseUp": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "target": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableTargets"
      },
      "required": false,
      "description": ""
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": true,
      "description": ""
    },
    "type": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableTypes"
      },
      "required": false,
      "description": ""
    },
    "width": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableWidths"
      },
      "required": false,
      "description": ""
    }
  }
};

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$1 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$1(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var Button = function (_Component) {
  _inherits$1(Button, _Component);

  function Button() {
    _classCallCheck$1(this, Button);

    return _possibleConstructorReturn$1(this, (Button.__proto__ || Object.getPrototypeOf(Button)).apply(this, arguments));
  }

  _createClass$1(Button, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          disabled = _props.disabled,
          icon = _props.icon,
          link = _props.link,
          onBlur = _props.onBlur,
          onClick = _props.onClick,
          onFocus = _props.onFocus,
          onHover = _props.onHover,
          onMouseDown = _props.onMouseDown,
          onMouseEnter = _props.onMouseEnter,
          onMouseLeave = _props.onMouseLeave,
          onMouseUp = _props.onMouseUp,
          stylesheet = _props.stylesheet,
          target = _props.target,
          title = _props.title,
          type = _props.type,
          width = _props.width,
          otherProps = _objectWithoutProperties$1(_props, ["disabled", "icon", "link", "onBlur", "onClick", "onFocus", "onHover", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseUp", "stylesheet", "target", "title", "type", "width"]);

      return React.createElement(
        ControlBehavior,
        {
          onBlur: onBlur,
          onFocus: onFocus,
          onMouseDown: onMouseDown,
          onMouseEnter: onMouseEnter,
          onMouseLeave: onMouseLeave,
          onMouseUp: onMouseUp
        },
        function (_ref) {
          var hasFocus = _ref.hasFocus,
              hasHover = _ref.hasHover,
              isPressed = _ref.isPressed,
              handleBlur = _ref.onBlur,
              handleFocus = _ref.onFocus,
              handleMouseDown = _ref.onMouseDown,
              handleMouseEnter = _ref.onMouseEnter,
              handleMouseLeave = _ref.onMouseLeave,
              handleMouseUp = _ref.onMouseUp;
          return React.createElement(ButtonPresenter, _extends$2({
            disabled: disabled,
            hasFocus: hasFocus,
            hasHover: hasHover,
            isPressed: isPressed,
            icon: icon,
            link: link,
            onBlur: handleBlur,
            onClick: onClick,
            onFocus: handleFocus,
            onHover: onHover,
            onMouseDown: handleMouseDown,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onMouseUp: handleMouseUp,
            stylesheet: stylesheet,
            target: target,
            title: title,
            type: type,
            width: width
          }, otherProps));
        }
      );
    }
  }]);

  return Button;
}(Component);

Button.propTypes = {
  /**
   * Prevents user interaction with the button
   */
  disabled: PropTypes.bool,
  /**
   * A @hig/icon element
   */
  icon: PropTypes.node,
  /**
   * Sets the link of a button
   */
  link: PropTypes.string,
  /**
   * Triggers blur when focus is moved away from icon
   */
  onBlur: PropTypes.func,
  /**
   * Triggers when you click the button
   */
  onClick: PropTypes.func,
  /**
   * Triggers when focus is moved to button
   */
  onFocus: PropTypes.func,
  /**
   * Triggers when you hover over the button
   */
  onHover: PropTypes.func,
  /**
   * Triggers when the user's mouse is pressed over the button
   */
  onMouseDown: PropTypes.func,
  /**
   * Triggers when the user's mouse is over the button
   */
  onMouseEnter: PropTypes.func,
  /**
   * Triggers when the user's mouse is no longer over the button
   */
  onMouseLeave: PropTypes.func,
  /**
   * Triggers when the user's mouse is no longer pressed over the button
   */
  onMouseUp: PropTypes.func,
  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func,
  /**
   * Specifies where to display the linked URL
   */
  target: PropTypes.oneOf(availableTargets),
  /**
   * Sets the title of a button
   */
  title: PropTypes.string.isRequired,
  /**
   * Specifies type of button
   */
  type: PropTypes.oneOf(availableTypes),
  /**
   * Specifies width of button
   */
  width: PropTypes.oneOf(availableWidths)
};
Button.defaultProps = {
  disabled: false,
  type: types.SOLID,
  width: widths.SHRINK
};
Button.__docgenInfo = {
  "description": "",
  "displayName": "Button",
  "props": {
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Prevents user interaction with the button",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "icon": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "A @hig/icon element"
    },
    "link": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Sets the link of a button"
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers blur when focus is moved away from icon"
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when you click the button"
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when focus is moved to button"
    },
    "onHover": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when you hover over the button"
    },
    "onMouseDown": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when the user's mouse is pressed over the button"
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when the user's mouse is over the button"
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when the user's mouse is no longer over the button"
    },
    "onMouseUp": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when the user's mouse is no longer pressed over the button"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    },
    "target": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableTargets"
      },
      "required": false,
      "description": "Specifies where to display the linked URL"
    },
    "title": {
      "type": {
        "name": "string"
      },
      "required": true,
      "description": "Sets the title of a button"
    },
    "type": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableTypes"
      },
      "required": false,
      "description": "Specifies type of button",
      "defaultValue": {
        "value": "types.SOLID",
        "computed": true
      }
    },
    "width": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableWidths"
      },
      "required": false,
      "description": "Specifies width of button",
      "defaultValue": {
        "value": "widths.SHRINK",
        "computed": true
      }
    }
  }
};

export default Button;
export { targets, types, widths, availableTargets, availableTypes, availableWidths };
